import { components } from '../../tokens/components/diamond/ED';
import { createTheme } from '../../tokens/createTheme';
import { shadows } from '../../tokens/shadows/diamond';
import { ED as bluestoneED } from '../bluestone/ED';
import breakpoints, { mediaQueries, screenMediaQueries } from '../breakpoints';
import accordionItemTokens from '../cobalt/components/accordionItem.tokens';
import alertTokens from '../cobalt/components/alert.tokens';
import boxTokens from '../cobalt/components/box.tokens';
import createCalendarTokens from '../cobalt/components/calendar.tokens';
import cardTokens from './components/card.tokens';
import checkboxTokens from '../cobalt/components/checkbox.tokens';
import decorationLineTokens from '../cobalt/components/decorationLine.tokens';
import drawerHeaderTokens from '../cobalt/components/drawerHeader.tokens';
import drawerHeaderContentTokens from '../cobalt/components/drawerHeaderContent.tokens';
import highlightedHeaderTokens from '../cobalt/components/highlightedHeader.tokens';
import inputTokens from '../cobalt/components/input.tokens';
import pillTokens from '../cobalt/components/pill.tokens';
import createPopoverTokens from '../cobalt/components/popover.tokens';
import sliderTokens from '../cobalt/components/slider.tokens';
import createTableTokens from '../cobalt/components/table.tokens';
import createTextWithIconTokens from '../cobalt/components/textWithIcon.tokens';
import type { DeprecatedBaseSpaces, DeprecatedTheme, Radii } from '../types';
import colors from './colors/ED';
import { space as baseSpace } from './spaces/base';
import { deprecatedSpace } from './spaces/deprecated';

const sizes = [16, 24, 32, 64, 128, 256];

export const space: DeprecatedBaseSpaces = {
  ...baseSpace,
  ...deprecatedSpace,
};

// TODO - Get rid of heading and body
// tokens and replace them with headings
// and bodies respectively
const lineHeights = {
  body: 1.5,
  bodies: {
    base: '20px',
    small: '18px',
    xSmall: '16px',
    xxSmall: '14px',
  },
  heading: 1.25,
  headings: {
    1: '36px',
    2: '28px',
    3: '24px',
    4: '22px',
    5: '20px',
    6: '18px',
  },
  display: {
    1: '48px',
    2: '44px',
    3: '40px',
  },
  overline: '16px',
};

const decorationLine = decorationLineTokens({ colors });

const popover = createPopoverTokens({});

export const radii: Radii = {
  small: '4px',
  regular: '8px',
  big: '16px',
  full: '9999px',
};

const fontSizeBody = [12, 14, 16, 18];

export const bodies = {
  base: 16,
  small: 14,
  xSmall: 12,
  xxSmall: 10,
};

export const headings = {
  1: 32,
  2: 24,
  3: 20,
  4: 18,
  5: 16,
  6: 14,
};

export const display = {
  1: 44,
  2: 40,
  3: 32,
};

export const fontWeights = {
  normal: 400,
  medium: 500,
  bold: 600,
};

const fontSizeHeading = [20, 24, 32];

// TODO - Get rid of heading and body
// tokens and replace them with headings
// and bodies respectively
export const fontSizes = {
  body: fontSizeBody,
  bodies,
  heading: fontSizeHeading,
  headings,
  display,
  overline: 12,
};

const borders = [0, '1px solid', '2px solid', '4px solid'];
const borderWidths = [0, 1, 2, 4, 8, 12];
const iconSizes = {
  tiny: 12,
  small: 16,
  base: 20,
  medium: 24,
  large: 32,
  xlarge: 64,
};

const drawerHeader = drawerHeaderTokens({
  colors,
  space,
  borders,
});

const drawerHeaderContent = drawerHeaderContentTokens({ borders, colors });

const box = boxTokens({ colors });

const card = cardTokens({ radii });

const highlightedHeader = highlightedHeaderTokens(lineHeights, fontSizes);

const pill = pillTokens({ colors });

const alert = alertTokens({ colors, borders });

const slider = sliderTokens({
  colors,
  sizes,
  space,
  fontSizeBody,
});

const checkbox = checkboxTokens({
  borders,
  fontSizeBody,
  sizes,
  space,
  colors,
});

const accordionItem = accordionItemTokens({
  borders,
  colors,
  space,
  fontSizeBody,
  fontSizeHeading,
  fontWeights,
  iconSizes,
});

const textWithIcon = createTextWithIconTokens({ colors, fontSizes, iconSizes });
const table = createTableTokens({ borderWidths });
const input = inputTokens({ fontSizes, lineHeights, colors });
const calendar = createCalendarTokens({ colors });

export const ED = {
  ...bluestoneED,
  colors: {
    ...colors,
    text: colors,
    drawerHeader: drawerHeader.colors,
    box: box.colors,
    decorationLine: decorationLine.colors,
    drawerHeaderContent: drawerHeaderContent.colors,
    pill: pill.colors,
    slider: slider.colors,
    checkbox: checkbox.colors,
    brandedProgressBar: colors.secondary['50'],
    textWithIcon: textWithIcon.color,
    accordionItem: accordionItem.colors,
    input: input.colors,
    alert: alert.colors,
    brandedHeaderBg: colors.primary['50'],
    calendar: calendar.colors,
  },
  sizes: {
    ...sizes,
    decorationLine: decorationLine.sizes,
    slider: slider.sizes,
    checkbox: checkbox.sizes,
    input: input.sizes,
    popover: popover.sizes,
  },
  shadows,
  breakpoints,
  mediaQueries,
  screenMediaQueries,
  space: {
    ...space,
    drawerHeader: drawerHeader.space,
    decorationLine: decorationLine.space,
    slider: slider.space,
    checkbox: checkbox.space,
    accordionItem: accordionItem.space,
    input: input.space,
    popover: popover.space,
  },
  fonts: {
    body: "'Poppins', -apple-system, '.SFNSText-Regular', 'San Francisco', 'Segoe UI', 'Helvetica Neue', 'Lucida Grande', sans-serif",
  },
  fontSizes: {
    ...fontSizes,
    slider: slider.fontSizes,
    checkbox: checkbox.fontSizes,
    textWithIcon: textWithIcon.fontSize,
    accordionItem: accordionItem.fontSizes,
    highlightedHeader: highlightedHeader.fontSizes,
    input: input.fontSizes,
  },
  fontWeights: {
    ...fontWeights,
    button: {
      default: fontWeights.medium,
    },
    accordionItem: accordionItem.fontWeights,
  },
  lineHeights: {
    ...lineHeights,
    checkbox: checkbox.lineHeight,
    highlightedHeader: highlightedHeader.lineHeights,
    input: input.lineHeight,
  },
  letterSpacings: {
    normal: 'normal',
    tracked: '0.1em',
    tight: '-0.05em',
    mega: '0.25em',
    body: '0',
    heading: ['-1px', '-0.5px', '0'],
    overline: '1.5px',
    display: '-1px',
    extended: '0.03em',
    condensed: '-0.03em',
    bitCondensed: '-0.02em',
    microCondensed: '-0.01em',
  },
  borders: {
    ...borders,
    drawerHeader: drawerHeader.borders,
    drawerHeaderContent: drawerHeaderContent.borders,
    checkbox: checkbox.borders,
    accordionItem: accordionItem.borders,
    alert: alert.borders,
  },
  borderWidths: {
    ...borderWidths,
    table: table.borderWidths,
  },
  borderStyles: {
    table: table.borderStyles,
  },
  radii: {
    ...radii,
    0: 0,
    1: 4,
    2: 8,
    3: 12,
    4: 16,
    5: '50%',
    6: '100%',
    7: '9999px',
    cardRadii: card.sizesRadii,
  },
  iconSizes: {
    ...iconSizes,
    textWithIcon: textWithIcon.iconSize,
    accordionItem: accordionItem.iconSizes,
  },
  inputWidths: {
    full: '100%',
    xxxl: 380,
    xxl: 300,
    xl: 270,
    l: 180,
    m: 100,
    s: 65,
    xs: 50,
  },
  inputHeights: {
    sm: 30,
    m: 40,
    l: 56,
  },
  zIndices: {
    stickyBox: 50,
    popup: 100,
    tooltip: 200,
    popover: 300,
    modal: 500,
  },
  transition: {
    slider: slider.transition,
  },
  opacity: {
    checkbox: checkbox.opacity,
  },
};

const theme: DeprecatedTheme = createTheme('diamond', ED, components);
export default theme;
